import React from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import { cn } from 'lib/utils';

interface Props {
  /** What the report is waiting for, e.g. "Waiting for your teacher to grade it". */
  label: string;
  /** Re-reads the report; the button is hidden when the surface cannot refresh. */
  onRefresh?: () => void;
  refreshing?: boolean;
  /** `dark` sits on the navy score panel, `light` on the plain page. */
  tone?: 'dark' | 'light';
  className?: string;
}

const DOT_DELAYS_MS = [0, 160, 320];

/** The "result is on its way" state of a report: a live indicator, the reason, and a refresh. */
export function GradingPendingStatus({ label, onRefresh, refreshing = false, tone = 'light', className }: Props) {
  const { t } = useTranslation();
  const refreshLabel = t('exam.report.refresh');

  return (
    <span role="status" className={cn('flex items-center gap-2.5', className)}>
      <span className="flex items-center gap-1" aria-hidden="true">
        {DOT_DELAYS_MS.map((delay) => (
          <span
            key={delay}
            className={cn(
              'h-1.5 w-1.5 rounded-full motion-safe:animate-pulse',
              tone === 'dark' ? 'bg-white/80' : 'bg-primary-600',
            )}
            style={{ animationDelay: `${delay}ms` }}
          />
        ))}
      </span>
      <span className="min-w-0 text-[15px] font-semibold">{label}</span>
      {onRefresh && (
        <button
          type="button"
          onClick={onRefresh}
          disabled={refreshing}
          title={refreshLabel}
          aria-label={refreshLabel}
          className={cn(
            'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-colors',
            'focus-visible:!outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            'disabled:cursor-default',
            tone === 'dark' ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-muted text-foreground hover:bg-muted/70',
          )}
        >
          <RefreshCw className={cn('h-4 w-4', refreshing && 'motion-safe:animate-spin')} aria-hidden="true" />
        </button>
      )}
    </span>
  );
}

export default GradingPendingStatus;
